// @flow

import React from 'react';
import type { Node } from 'react';
import { splitName } from '@riseart/fe-utils';
import { meta as META_ENUM } from 'Enum';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';

type Props = {
  hasError: boolean,
  noIndex?: boolean,
  translationKeyPrefix: string,
  artistData: Object,
  mainBannerImage: Object,
  canonicalURL: ?string,
  subpageCanonicalRedirect: ?string,
  hrefLangRedirects: Object,
  formatMessage: Function,
  translationMetas?: { metaTitle?: boolean, metaDescription?: boolean, metaKeywords?: boolean },
  additionalMetas?: Object,
};

/**
 * ArtistDetailMeta
 * @param {Props} props
 */
export const ArtistDetailMeta = ({
  hasError,
  noIndex = false,
  translationKeyPrefix,
  artistData,
  mainBannerImage,
  canonicalURL,
  subpageCanonicalRedirect,
  hrefLangRedirects,
  formatMessage,
  translationMetas = { metaTitle: true, metaDescription: true, metaKeywords: true },
  additionalMetas = {},
}: Props): Node => {
  const { name } = artistData;
  const { firstName, lastName } = splitName(name);
  const metaData = {
    title:
      artistData.metaTitle ||
      (translationMetas.metaTitle &&
        formatMessage({ id: `${translationKeyPrefix}.dynamicMetaTitle` }, { name })) ||
      null,
    description:
      artistData.metaDescription ||
      (translationMetas.metaDescription &&
        formatMessage({ id: `${translationKeyPrefix}.dynamicMetaDescription` }, { name })) ||
      null,
    keywords:
      artistData.metaKeywords ||
      (translationMetas.metaKeywords &&
        formatMessage({ id: `${translationKeyPrefix}.dynamicMetaKeywords` }, { name })) ||
      null,
  };

  return (
    <MetaProvider
      metaSubscriber={META_SUBSCRIBER_NAME.ARTIST_META}
      meta={
        artistData
          ? {
              [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
              [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: subpageCanonicalRedirect,
              [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: hrefLangRedirects,
              [META_ENUM.METATYPE.META_TITLE]: metaData.title,
              [META_ENUM.METATYPE.META_DESCRIPTION]: metaData.description,
              [META_ENUM.METATYPE.META_KEYWORDS]: metaData.keywords,
              [META_ENUM.METATYPE.META_OG_TITLE]: metaData.title,
              [META_ENUM.METATYPE.META_OG_DESCRIPTION]: metaData.description,
              [META_ENUM.METATYPE.META_OG_URL]: canonicalURL,
              [META_ENUM.METATYPE.META_OG_PROFILE_FIRST_NAME]: firstName,
              [META_ENUM.METATYPE.META_OG_PROFILE_LAST_NAME]: lastName,
              ...(artistData.gender
                ? {
                    [META_ENUM.METATYPE.META_OG_PROFILE_GENDER]:
                      artistData.gender.toLowerCase() === 'm' ? 'male' : 'female',
                  }
                : {}),
              ...(noIndex
                ? { [META_ENUM.METATYPE.META_ROBOTS]: META_ENUM.ATTRIBUTE_CONTENT.NO_INDEX }
                : {}),
              ...(mainBannerImage
                ? {
                    [META_ENUM.METATYPE.META_OG_IMAGE]: generateSrcForCDN({
                      ...mainBannerImage,
                      width: 1200,
                    }),
                  }
                : {}),
              [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTIST]: generateLDJSON(LDTYPES.ARTIST, {
                ...artistData,
                url: canonicalURL,
                mainEntityOfPage: {
                  type: 'WebPage',
                  url: canonicalURL,
                },
              }),
              ...(additionalMetas || {}),
              ...(artistData.meta || {}),
            }
          : null
      }
      hasError={hasError}
    />
  );
};
